import { Button, Input, Label } from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { RefreshCw } from "@/components/icons";
import type { SetupStepProps } from "@/components/setup/steps";
import { StepSaveButton, YesNoStep } from "@/components/setup/yes-no-step";
import { setMyDjPin, updateMySiteConfig } from "@/lib/api";
import { toAppError } from "@/lib/errors";

const PIN_RE = /^[A-Za-z0-9]{4,12}$/;

/** Unambiguous alphanumerics (no 0/O, 1/I/l) for a readable PIN. */
function suggestPin(): string {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  return Array.from(
    { length: 6 },
    () => chars[Math.floor(Math.random() * chars.length)],
  ).join("");
}

/**
 * Wizard DJ step — guests suggest and upvote songs; the DJ opens the
 * request list with a PIN so only they see it. Saves the PIN (hashed
 * server-side) and flips the feature toggle.
 */
export function StepDj({
  orderId,
  setup,
  linear,
  onDone,
  onBack,
}: SetupStepProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { site, config, djPinSet } = setup;
  const siteId = site?.siteId ?? "";

  const [enabled, setEnabled] = useState<boolean | null>(
    config?.permissions?.allowDjRequests === undefined && !djPinSet
      ? null
      : (config?.permissions?.allowDjRequests ?? false),
  );
  const [pin, setPin] = useState(() => (djPinSet ? "" : suggestPin()));

  const mutation = useMutation({
    mutationFn: async () => {
      if (enabled === false) {
        await updateMySiteConfig({
          siteId,
          configPatch: { permissions: { allowDjRequests: false } },
        });
        return;
      }
      // Keep the existing PIN when the field is left empty on re-entry.
      if (pin.trim() !== "" || !djPinSet) {
        await setMyDjPin({ siteId, code: pin.trim() });
      }
      await updateMySiteConfig({
        siteId,
        configPatch: { permissions: { allowDjRequests: true } },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mySetup", orderId] });
      onDone();
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const save = () => {
    if (enabled === null) {
      toast.error(t("setup.pickYesNo"));
      return;
    }
    if (enabled) {
      const code = pin.trim();
      const keepingExisting = code === "" && djPinSet;
      if (!keepingExisting && !PIN_RE.test(code)) {
        toast.error(t("setup.dj.pinInvalid"));
        return;
      }
    }
    mutation.mutate();
  };

  return (
    <YesNoStep
      stepId="dj"
      yesLabel={t("setup.dj.yes")}
      noLabel={t("setup.dj.no")}
      enabled={enabled}
      onChoose={setEnabled}
      onBack={onBack}
      footer={
        <StepSaveButton
          saving={mutation.isPending}
          disabled={enabled === null}
          linear={linear}
          onClick={save}
        />
      }
    >
      <div className="space-y-2">
        <Label htmlFor="setup-dj-pin" className="text-base">
          {t("setup.dj.pinLabel")}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id="setup-dj-pin"
            autoComplete="off"
            spellCheck={false}
            placeholder={djPinSet ? t("setup.dj.keepCurrent") : undefined}
            value={pin}
            onChange={(e) => setPin(e.target.value.toUpperCase())}
            className="h-11 max-w-48 font-mono text-base tracking-widest"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => setPin(suggestPin())}
          >
            <RefreshCw className="h-4 w-4" /> {t("setup.dj.newPin")}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          {djPinSet
            ? t("setup.dj.pinAlreadySet")
            : t("setup.dj.givePinToDj")}
        </p>
      </div>
    </YesNoStep>
  );
}
